import { contentQualityAttributes } from "@/lib/seo/content-quality";

interface MethodologyTrustBlockProps {
  /**
   * What this page computes, woven into the opening line — written so it reads
   * as the sentence subject. E.g. "Your Gun Milan score", "Today's panchang",
   * "Your birth chart".
   */
  computes: string;
  /** Heading override (default: "The Engine Behind This Reading"). */
  title?: string;
  /**
   * Whether to render the interpretation-grounding paragraph (classical BPHS
   * rule + Pandit-ji). Defaults true for reading surfaces (chart, matching,
   * dosha, transit). Set false on pure-timing pages (panchang, rahu-kaal,
   * choghadiya) that show computed times and offer no "reading".
   */
  interpretation?: boolean;
  /** Optional surface-specific method note rendered as a closing line. */
  note?: string;
}

/**
 * Engine / methodology E-E-A-T trust block for tool pages (rules 22 / 29 / 44,
 * ADR-0022). The single reusable place that states, honestly, HOW Ishvaram
 * produces a Vedic figure: the Swiss Ephemeris + Lahiri ayanamsa engine,
 * calibrated against the governed cross-source almanac reference set, with
 * interpretation anchored to classical Brihat Parashara Hora Shastra rules.
 *
 * Attribution is ROLE-BASED — "Your AI Jyotishi — Pandit-ji" + the Ishvaram
 * Jyotish desk. It NEVER fabricates a human byline, credentials, lineage, or
 * photo (ADR-0022): it claims only true things (grounded in BPHS, Swiss
 * Ephemeris precision, an AI that never pretends to be human).
 *
 * Adopt on any tool page that lacks a methodology section; pages with their own
 * engine write-up (free-kundali, prashna-kundali) don't need it.
 */
export default function MethodologyTrustBlock({
  computes,
  title,
  interpretation = true,
  note,
}: MethodologyTrustBlockProps) {
  return (
    <section
      className="mt-10"
      {...contentQualityAttributes({
        facet: "evidence",
        value: "methodology",
        evidenceSource: "methodology:calculation-engine",
      })}
    >
      <h2 className="mb-4 font-serif text-title font-bold text-text-primary">
        {title ?? "The Engine Behind This Reading"}
      </h2>
      <p className="text-body-small leading-relaxed mb-4">
        {computes} is computed by the same astronomical engine a serious Jyotishi relies on — the{" "}
        <strong>Swiss Ephemeris</strong>, which fixes every planet&apos;s position to sub-arcsecond
        precision, read against the <strong>Lahiri ayanamsa</strong> (the sidereal zodiac Vedic
        astrology uses). No fixed-calendar shortcuts, no rounded almanac tables.
      </p>
      <p className="text-body-small leading-relaxed mb-4">
        We calibrate that engine to within ±5 minutes on every time-of-day value, and an exact match
        on tithi and nakshatra names — so the figures you read here are the same ones your family
        astrologer would quote from the panchang.
      </p>
      {interpretation ? (
        <p
          className="text-body-small leading-relaxed mb-4"
          {...contentQualityAttributes({ facet: "who" })}
        >
          The meaning layered on top is not generic AI text. Every reading is anchored to a
          classical rule from the <strong>Brihat Parashara Hora Shastra</strong> and the Parashari
          tradition, then explained in plain language by{" "}
          <strong>Your AI Jyotishi — Pandit-ji</strong>. The planets and yogas come from the engine;
          the words only make them clear.
        </p>
      ) : null}
      {note ? <p className="text-body-small leading-relaxed">{note}</p> : null}
    </section>
  );
}
